import { useCallback, useEffect, useMemo, useState } from "react";
import FilterMenu from "../../../components/FilterMenu";
import Loading from "../../../components/Loading";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import {
  lightenColor,
  ReMixChart,
} from "../../../components/recharts/ReMixCharts";
import { stringToConsistentColor } from "../../../extensions/string";
import { BrushStart } from "../../../models/chart_model";
import { getGridcoDiscomData, getGridcoScheduleData } from "../../../Rest_api/RestApi";
import { ExchangeColors } from "../../Dashboard/Exchange3/FormatData";
import { R_CHART_TYPES } from "../const";
import { GROUP_BY } from "./Data";
import { GridcoDiscomData } from "../../../models/gridco";
import { DISCOM } from "./Data";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import { renderHourTick } from "../../../components/recharts/components";
import { useSelector } from "react-redux";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "../../Rajasthan/Demand/SecondaryHeader";
import Tooltip from "../../../components/tooltip";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import { GridcoPeakAnalysis } from "./Peak_Discom";
import { GridcoSummaryAnalysis } from "./Discom_Analysis_Summary";

const UNITS = ["MW", "MWh"];

let X_AXIS = [
  {
    label: "Time Slot",
    key: "time",
    values: ["block", "date"],
    isTime: true,
  },
  {
    label: "Date",
    key: "date",
    values: ["date"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    values: ["month"],
    isTime: false,
  },
];

const X_AXIS_MODES = [
  { label: "Intervals", value: "intervals" },
  { label: "Peak Analysis", value: "peak" },
  { label: "Summary", value: "summary" }
];

const TYPE = ["Drawal", "Schedule"];

export function GridcoDiscomAnalysis() {
  const [TableData, setTableData] = useState<any[]>([]);
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates
  );

  const [selectedXAxisMode, setSelectedXAxisMode] = useState(X_AXIS_MODES[0]);
  const [drawalData, setDrawalData] = useState<GridcoDiscomData[]>([]);
  const [scheduleData, setScheduleData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [selectedUnit, setSelectedUnit] = useState(UNITS[0]);
  const [selectedInterval, setSelectedInterval] = useState(X_AXIS[0]);
  const [selectedDiscom, setSelectedDiscom] = useState<any[]>([]);
  const [selectedArea, setSelectedArea] = useState<any[]>([]);
  const [uniqueDate, setUniqueDate] = useState<any[]>([]);
  const [selectedDate, setSelectedDate] = useState<any[]>([]);
  const [uniqueBlock, setUniqueBlock] = useState<any[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(R_CHART_TYPES[2]);
  const [selectedGroupKeys, setSelectedGroupKeys] = useState(GROUP_BY[0]);
  const [loading, setLoading] = useState(true);

  const chartSuffixes = [
    { suffix: "drawal", displayName: "Drawal", stackSuffix: "drawal" },
    { suffix: "schedule", displayName: "Schedule", stackSuffix: "schedule" },
  ];

  const createChartObject = (
    key: string,
    index: number,
    suffix: string,
    displayName: string,
    stackSuffix: string,
    selectedXaxis: { isTime: boolean },
    selectedChartType: { value: string; stackId?: string },
    ExchangeColors: string[]
  ) => {
    return {
      dataKey: `${key}_${suffix}_${selectedUnit.toLowerCase()}`,
      name: `${key.replace("_", " ").replace("undefined", "")} ${displayName}`,
      legendColor: undefined,
      stroke:
        suffix === "schedule"
          ? lightenColor(ExchangeColors[index], 25)
          : suffix === "drawal"
          ? ExchangeColors[index]
          : stringToConsistentColor(`${key}_${suffix}`),
      type: selectedChartType.value,
      stackId:
        selectedChartType.stackId !== undefined ? stackSuffix : undefined,
    };
  };

  const [selectedSuffix, setSelectedSuffix] = useState([chartSuffixes[0]]);

  const _handlefetchData = useCallback(async () => {
    if (DefaultDates[activeTab].defaultRangeDate === undefined) {
      return;
    }
    setLoading(true);
    const dates = DefaultDates[activeTab].defaultRangeDate;
    const startDate = dates.startDate.toISOString().split("T")[0];
    const endDate = dates.endDate.toISOString().split("T")[0];

    try {
      // Always fetch both data sets
      const [fetchedDrawalData, fetchedScheduleData] = await Promise.all([
        getGridcoDiscomData(startDate, endDate),
        getGridcoScheduleData(startDate, endDate)
      ]);

      setDrawalData(fetchedDrawalData);
      setScheduleData(fetchedScheduleData);

      // Set unique dates and blocks from drawal data
      const allDates = Array.from(
        new Set([
          ...fetchedDrawalData.map((item) => item.date),
          ...fetchedScheduleData.map((item) => item.date)
        ])
      ).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

      const allBlocks = Array.from(
        new Set([
          ...fetchedDrawalData.map((item) => item.block),
          ...fetchedScheduleData.map((item) => item.block)
        ])
      ).sort((a, b) => a - b);

      setUniqueDate(allDates);
      setUniqueBlock(allBlocks);
    } catch (error) {
      console.error("Error fetching data:", error);
    }

    setLoading(false);
  }, [DefaultDates, activeTab]);

  function _handleGroupByChange(index: number) {
    setSelectedGroupKeys(GROUP_BY[index]);
  }

  useEffect(() => {
    const isDrawalSelected = selectedSuffix.some(s => s.suffix === "drawal");
    const isScheduleSelected = selectedSuffix.some(s => s.suffix === "schedule");

    // Step 1: Create a complete base dataset from both sources
    const allKeysSet = new Set<string>();
    
    // Collect all unique combinations of date, block, and discom from both datasets
    [...drawalData, ...scheduleData].forEach(item => {
      const key = `${item.date}_${item.block}_${item.discom}`;
      allKeysSet.add(key);
    });

    // Step 2: Build merged data ensuring all combinations exist
    const mergedDataMap = new Map();
    
    allKeysSet.forEach(key => {
      const [date, blockStr, discom] = key.split('_');
      const block = parseInt(blockStr);
      
      // Find matching records from both datasets
      const drawalRecord = drawalData.find(
        d => d.date === date && d.block === block && d.discom === discom
      );
      const scheduleRecord = scheduleData.find(
        s => s.date === date && s.block === block && s.discom === discom
      );

      // Use drawal record as base, or schedule if drawal doesn't exist
      const baseRecord = drawalRecord || scheduleRecord || {};
      
      mergedDataMap.set(key, {
        date: date,
        block: block,
        discom: discom,
        // Drawal data
        drawal_mw: drawalRecord?.drawal_mw || 0,
        drawal_mwh: drawalRecord?.drawal_mwh || 0,
        // Schedule data
        schedule_mw: scheduleRecord?.schedule_mw || 0,
        schedule_mwh: scheduleRecord?.schedule_mwh || 0,
        // Copy other metadata fields
        month: baseRecord.month,
        dateIndex: baseRecord.dateIndex,
        dates: baseRecord.dates,
      });
    });

    let dataToProcess = Array.from(mergedDataMap.values());

    // Step 3: Filter based on selected type
    // If only drawal is selected, keep only records with drawal data
    if (isDrawalSelected && !isScheduleSelected) {
      dataToProcess = dataToProcess.filter(item => item.drawal_mw > 0);
    }
    // If only schedule is selected, keep only records with schedule data
    else if (!isDrawalSelected && isScheduleSelected) {
      dataToProcess = dataToProcess.filter(item => item.schedule_mw > 0);
    }
    // If both are selected, keep all records (some may have only drawal, some only schedule, some both)

    // Step 4: Apply user filters
    let filteredData = dataToProcess;

    if (selectedDiscom.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedDiscom.includes(item.discom)
      );
    }
    if (selectedDate.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedDate.map((item) => item.value).includes(item.date)
      );
    }
    if (selectedBlock.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedBlock.map((item) => item.value).includes(item.block)
      );
    }

    // Step 5: Sort by date and block to ensure consistency
    filteredData.sort((a, b) => {
      const dateCompare = new Date(a.date).getTime() - new Date(b.date).getTime();
      if (dateCompare !== 0) return dateCompare;
      return a.block - b.block;
    });

    function getKeys() {
      if (selectedInterval.key === "time") {
        return ["date", "block"];
      } else if (selectedInterval.key === "date") {
        return ["date"];
      } else {
        return [selectedInterval.values[0]];
      }
    }

    // Step 6: Build aggregation keys dynamically based on selected suffix
    const aggregationKeys: string[] = [];
    const averageKeys: string[] = [];
    
    selectedSuffix.forEach(({ suffix }) => {
      if (suffix === "drawal") {
        aggregationKeys.push("drawal_mw", "drawal_mwh");
        averageKeys.push("drawal_mw");
      }
      if (suffix === "schedule") {
        aggregationKeys.push("schedule_mw", "schedule_mwh");
        averageKeys.push("schedule_mw");
      }
    });

    const par: ChartDataParams = {
      keys: getKeys(),
      groupBy: [selectedGroupKeys.toLowerCase()],
      tableKeys: ["date", "block"],
      volumeKey: ``,
      aggregationKeys: aggregationKeys,
      averageKeys: averageKeys,
      averageDuplicationKeys: ["date", "block"],
      priceKey: "",
      data: filteredData,
    };
    
    const cData = FormatChartData(par);
    setTableData(cData.tableData || []);
    setChartData(cData.data);
    setChartKeys(cData.keys);
  }, [
    drawalData,
    scheduleData,
    selectedGroupKeys,
    selectedDiscom,
    selectedArea,
    selectedDate,
    selectedBlock,
    selectedInterval,
    selectedUnit,
    selectedSuffix,
  ]);

  function _handleSuffixChange(index: number) {
    const selectedItem = chartSuffixes[index];
    
    if (selectedSuffix.some(item => item.suffix === selectedItem.suffix)) {
      // If only one item is selected, don't remove it
      if (selectedSuffix.length === 1) {
        return;
      }
      // Remove the item
      setSelectedSuffix(selectedSuffix.filter(item => item.suffix !== selectedItem.suffix));
    } else {
      // Add the item
      setSelectedSuffix([...selectedSuffix, selectedItem]);
    }
  }

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  const handlexDataKey = useCallback(() => {
    if (selectedInterval.isTime) {
      if (selectedBlock.length > 0) {
        if (selectedInterval.isTime) {
          return "block";
        } else {
          return selectedInterval.values[0];
        }
      }
      return "block";
    } else {
      return selectedInterval.key;
    }
  }, [
    selectedBlock.length,
    selectedInterval.isTime,
    selectedInterval.key,
    selectedInterval.values,
  ]);

  const xDataKey = useMemo(() => handlexDataKey(), [handlexDataKey]);

  function handleUnitChange(params: string) {
    setSelectedUnit(params);
  }

  function handleXAxisModeChange(item: string, index: number) {
    setSelectedXAxisMode(X_AXIS_MODES[index]);
  }

  function getLegends() {
    return selectedSuffix.flatMap(({ suffix, displayName, stackSuffix }) =>
      chartKeys.map(
        (key, index) =>
          createChartObject(
            key,
            index,
            suffix,
            displayName,
            stackSuffix,
            selectedInterval,
            selectedChartType as any,
            ExchangeColors
          ) as any
      )
    );
  }

  function secondDataKey() {
    if (selectedInterval.isTime) {
      if (selectedBlock.length > 0) {
        return "date";
      }
      return "date";
    } else {
      return undefined;
    }
  }

  const summaryExtraKeys = selectedInterval.isTime
    ? [
        "block",
        selectedInterval.values[1],
      ]
    : [selectedInterval.values[0]];

  const summaryExtraLabels = selectedInterval.values.map(
    (value) => (value === "date" ? "Date" : value)
  );
  
  const tablePar = {
    legends: getLegends(),
    data: chartData,
    tableData: TableData,
    itemsPerPage: 96,
    unit: selectedUnit,
    extraKeys: summaryExtraKeys,
    extraLabels: summaryExtraLabels,
    filename: "Discom Load",
  };

  if (selectedXAxisMode.value === "peak") {
    return (
      <GridcoPeakAnalysis 
        onXAxisModeChange={handleXAxisModeChange}
        selectedUnit={selectedUnit}
        onUnitChange={handleUnitChange}
      />
    );
  }

  if (selectedXAxisMode.value === "summary") {
    return (
      <GridcoSummaryAnalysis 
        onXAxisModeChange={handleXAxisModeChange}
        selectedUnit={selectedUnit}
        onUnitChange={handleUnitChange}
      />
    );
  }

  return (
    <div className="gas-page-container">
      {loading && <Loading />}
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="X-Axis"
            items={X_AXIS_MODES.map((item) => item.label)}
            selectedItems={[selectedXAxisMode.label]}
            onSelect={handleXAxisModeChange}
          />

          <FilterMenu
            title="Interval"
            items={X_AXIS.map((item) => item.label)}
            selectedItems={[selectedInterval.label]}
            onSelect={(item, index) => {
              setSelectedInterval(X_AXIS[index]);
            }}
          />

          <FilterMenu
            title="Unit"
            items={UNITS}
            selectedItems={[selectedUnit]}
            onSelect={(item, index) => {
              handleUnitChange(item);
            }}
          />
          <FilterMenu
            title="Discom"
            items={DISCOM.map((item) => item.label)}
            selectedItems={selectedDiscom}
            onSelect={(item, index) => {}}
            setSelectedItems={setSelectedDiscom}
          />
          <FilterMenu
            title="Group By"
            items={GROUP_BY}
            selectedItems={[selectedGroupKeys]}
            onSelect={(item, index) => {
              if (index === 1) return
              _handleGroupByChange(index);
            }}
          />
          <FilterMenu
            title="Type"
            items={TYPE}
            selectedItems={selectedSuffix.map((item) => item.displayName)}
            onSelect={(item, index) => {
              _handleSuffixChange(index);
            }}
          />
          <FilterMenu
            title="Chart Type"
            items={R_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) => {
              setSelectedChartType(R_CHART_TYPES[index]);
            }}
          />
        </div>
      </NewFilterContainer>

      <MainPageContainer>
        <SecondaryHeader />
        <div className="gas-chart-container">
          <div className="gas-heading gap-x-10">
            <h2 className="chartHeading w-60">Discom Load (MW)</h2>
            <div className="flex gap-x-6 w-full justify-end">
              <BlockRangeSelector
                blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                placeholder="Select Block"
                selectedOptions={selectedBlock.map((item)=>item.value)}
                setSelectedOptions={setSelectedBlock}
              />

              <button
                onClick={() => {
                  dataTab.label === "Chart" && downloadChartTableCSV(tablePar);
                  dataTab.label === "Summary" && downloadSummaryCSV(tablePar);
                  dataTab.label === "Table" && downloadChartTableCSV(tablePar);
                }}
                className="downloadButton"
              >
                Download Data
              </button>
            </div>
          </div>

          {dataTab.label === "Chart" && (
            <ReMixChart
              data={chartData}
              showBrush={chartData.length > 96 ? true : undefined}
              brushStart={BrushStart.Start}
              isTimeSlot={selectedInterval.isTime}
              onlyTitle={true}
              barCategoryGap={10}
              unit={selectedUnit}
              yAxisLabel={selectedUnit}
              legends={getLegends()}
              xTick={
                selectedInterval.isTime && selectedBlock.length === 0
                  ? (value: any, index: any) => {
                      return renderHourTick(value, 10);
                    }
                  : undefined
              }
              xDataKey={xDataKey}
              secondXDataKey={secondDataKey()}
            />
          )}
          {dataTab.label === "Summary" && <LegendSummaryTable {...tablePar} />}
          {dataTab.label === "Table" && <ChartTable {...tablePar} />}
        </div>
        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5 ">
            <Tooltip
              note={<p>Gridco : BSP bills & ABT Meters</p>}
              text="1. BSP Bills : Annexure 1 is used for cumulation.
              2. Some meter numbers in Annexure 1 were missing in provided ABT Meter files.
"
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}